import React from 'react';
import { annotateFurigana, groupSegmentsByRanges, parseDecoratedText } from '../utils/furigana';
import { useFurigana } from '../contexts/FuriganaContext';

// Renders content with furigana (semantic <ruby><rt>) over every covered kanji.
// It RECURSES into composite children so each STRING LEAF is annotated — wrapping
// a subtree (e.g. a heading, a paragraph, an array of <span>/<strong> from a
// markdown/highlight renderer) annotates all its text leaves, not just a
// top-level string. When the toggle is OFF, content renders unchanged.
//
// The base kanji stay in the DOM as text (readings are userSelect:none), so plain
// reading, text selection and the screen-reader base name are unchanged.

// HTML tags whose text must NOT be HTML-ruby-annotated here: SVG (uses the
// dedicated SVG reading renderer), form controls, and non-text / already-ruby
// elements.
const SKIP_TAGS = new Set([
  'svg', 'input', 'textarea', 'select', 'option', 'ruby', 'rt', 'rp',
  'script', 'style', 'code', 'pre', 'canvas', 'img', 'br', 'hr',
]);

// Render pre-computed furigana segments (from annotateFurigana) as <ruby><rt>. Exported so callers
// that must tokenize a WHOLE string once and then slice it (e.g. ProblemTextPanel's keyword highlighting
// over research stimulus — re-tokenizing each fragment would lose the context its reading rules need)
// can reuse the exact same ruby markup.
export function renderFuriganaSegments(segs, keyPrefix = 'f') {
  if (!Array.isArray(segs) || segs.length === 0 || !segs.some((seg) => seg && seg.reading)) {
    // nothing to annotate → the plain concatenated text (base/text reconstruct the input exactly)
    return (segs || []).map((seg) => (seg == null ? '' : (seg.base != null ? seg.base : seg.text))).join('');
  }
  return segs.map((seg, i) =>
    seg.reading ? (
      <ruby key={`${keyPrefix}r${i}`}>
        {seg.base}
        {/* aria-hidden: the base kanji carries the accessible name (screen readers read
            it via their own dictionary); the visual reading must not pollute a control's
            accessible name nor be double-announced. */}
        <rt aria-hidden="true" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>{seg.reading}</rt>
      </ruby>
    ) : (
      <React.Fragment key={`${keyPrefix}t${i}`}>{seg.text}</React.Fragment>
    )
  );
}

// Component-supplied UI-label readings (方案C). A static UI label whose reading is unresolvable from the lone
// token in general text — 中=ちゅう as a font-size vs なか "inside", 方=かた "person" vs ほう, 色=いろ vs the
// しょく suffix — is NOT corrected by the shared engine, which also runs over uploaded research stimulus and
// so must stay universal. Instead the component that renders the label passes overrides={{surface: reading}}
// and we force that reading onto any segment whose whole base/text is exactly `surface`. Pass an override
// ONLY for a label where the target kanji is a STANDALONE token (its own word: the size 中, the person 方, the
// suffix 色) — a key that also appears as a kanji run inside a LONGER word in the SAME label (e.g. 大 inside
// 大きい) would be rewritten too. Scoped to one render instance, so dynamic text (rendered by other instances
// or the raw engine) is never affected.
function applyOverrides(segs, overrides) {
  return segs.map((seg) => {
    const surface = seg.base != null ? seg.base : seg.text;
    if (surface != null && Object.prototype.hasOwnProperty.call(overrides, surface)) {
      return { base: surface, reading: overrides[surface] };
    }
    return seg;
  });
}

function renderString(s, keyPrefix, overrides) {
  let segs = annotateFurigana(s);
  if (overrides) segs = applyOverrides(segs, overrides);
  // Nothing to annotate (no kanji, or none covered) → return the plain string,
  // avoiding needless wrapper nodes.
  if (segs.length === 0 || !segs.some((seg) => seg.reading)) return s;
  return renderFuriganaSegments(segs, keyPrefix);
}

function annotateNode(node, keyPrefix, overrides) {
  if (typeof node === 'string') return renderString(node, keyPrefix, overrides);
  if (typeof node === 'number') return renderString(String(node), keyPrefix, overrides);
  if (Array.isArray(node)) return node.map((c, i) => annotateNode(c, `${keyPrefix}.${i}`, overrides));
  if (React.isValidElement(node)) {
    // Only recurse into intrinsic HTML elements (string type). Custom components
    // (function/object type) manage their own rendering and must annotate their
    // own leaves; cloning them from outside could break them. Skip SVG/control/
    // ruby tags. cloneElement(el, undefined, ...) preserves props/ref/key.
    if (typeof node.type !== 'string' || SKIP_TAGS.has(node.type)) return node;
    const kids = node.props && node.props.children;
    if (kids == null) return node;
    return React.cloneElement(node, undefined, annotateNode(kids, `${keyPrefix}c`, overrides));
  }
  return node; // null / boolean / undefined / function
}

// A SELECTED fragment (e.g. a highlight chip echoing text the student picked out of a message/explanation),
// rendered with the reading it has IN ITS SOURCE rather than re-tokenized in isolation: selecting 副 out of
// 主・副 must still show ふく (a lone 副 reads そい), and 間 out of 読んでいる間 must show あいだ (Codex R17 #5).
// The chip `text` was selected from the RENDERED (markdown-stripped) body, so strip the raw source's **bold**
// markers before locating it — else a marker sitting at a context boundary (読んでいる**間**) would isolate 間
// and mis-read it (Codex R18 #2). Uses the fragment's FIRST occurrence in the stripped source; because the app
// highlights ALL occurrences of the string, a duplicate surface that reads differently in two spots is a known
// limitation (R18 #3). Falls back to the isolated fragment if not found. Respects the furigana toggle.
export function FuriganaFragment({ source, text, bareRanges = [] }) {
  const { enabled } = useFurigana();
  if (text == null) return null;
  if (!enabled) return <>{text}</>;
  const frag = typeof text === 'string' ? text : String(text);
  // Normalize source and identity ranges together. Markdown-like characters inside an identity are literal,
  // while chrome markdown outside it is stripped; this is the same coordinate transform used by the message.
  const parsed = parseDecoratedText(typeof source === 'string' ? source : '', bareRanges);
  const src = parsed.plain;
  const normalizedBareRanges = parsed.protectedRanges;
  const at = src ? src.indexOf(frag) : -1;
  if (at < 0) {
    // If identity metadata exists but the selected text can no longer be mapped
    // to its source, correctness-first means rendering the fragment bare rather
    // than guessing that the name is absent.
    if (Array.isArray(bareRanges) && bareRanges.length > 0) return <>{frag}</>;
    return <>{renderFuriganaSegments(annotateFurigana(frag), 'ff')}</>;
  }

  const end = at + frag.length;
  const clippedBare = normalizedBareRanges
    .map((rg) => ({ start: Math.max(at, rg.start), end: Math.min(end, rg.end) }))
    .filter((rg) => Number.isFinite(rg.start) && Number.isFinite(rg.end) && rg.end > rg.start);
  const bounds = new Set([at, end]);
  clippedBare.forEach((rg) => { bounds.add(rg.start); bounds.add(rg.end); });
  const cuts = [...bounds].sort((a, b) => a - b);
  const ranges = [];
  for (let i = 0; i < cuts.length - 1; i += 1) {
    const start = cuts[i];
    const rangeEnd = cuts[i + 1];
    if (rangeEnd <= start) continue;
    ranges.push({
      start,
      end: rangeEnd,
      meta: { isIdentity: clippedBare.some((rg) => rg.start <= start && rangeEnd <= rg.end) },
    });
  }
  const combineMeta = (metas) => ({ isIdentity: metas.some((meta) => meta && meta.isIdentity) });
  const groups = groupSegmentsByRanges(src, ranges, combineMeta).filter((group) => group.meta);
  return <>{groups.map((group, i) => (
    <React.Fragment key={`ffg${i}`}>
      {group.meta.isIdentity
        ? group.segs.map((seg) => (seg.base != null ? seg.base : seg.text)).join('')
        : renderFuriganaSegments(group.segs, `ff${i}`)}
    </React.Fragment>
  ))}</>;
}

// NOTE: annotateFurigana holds ONLY universal readings (see furigana.js) — UI-label readings that would be
// wrong on uploaded stimulus (中/方/空/色) are supplied here per render via the `overrides` prop, not by the
// engine. The ONLY hard rule for callers is: never re-tokenize a fragment. A caller that decorates/splits
// prose (keyword highlight, markdown) must annotate the WHOLE string once and project segments onto its
// ranges — see groupSegmentsByRanges / projectDecoratedText (ProblemTextPanel / ChatInterface / InteractiveGraph).
export default function FuriganaText({ children, text, overrides }) {
  const { enabled } = useFurigana();
  const content = text != null ? text : children;
  if (content == null) return null;
  if (!enabled) return <>{content}</>;
  return <>{annotateNode(content, 'f', overrides)}</>;
}
